import React, { useState, useEffect } from 'react';
import { IconUsers, IconCalendarEvent, IconBuildingStore, IconEye } from '@tabler/icons-react';
import styles from '../../pages/AdminDashboard.module.css';
import { adminService } from '../../services/adminApi';
import { categoryService } from '../../services/categoryService';

interface DashboardStatsProps {
  onError?: (message: string) => void;
}

// Types pour les statistiques
interface DashboardStats {
  users: {
    total: number;
    active: number;
    newThisMonth: number;
    byRole: {
      student: number;
      organization: number;
      admin: number;
    };
  };
  events: {
    total: number;
    upcoming: number;
    viewsThisMonth?: number;
    byCategory: Record<string, number>;
  };
  organizations: {
    total: number;
    verified: number;
    pendingVerification: number;
  };
}

// Statistiques par défaut
const defaultStats: DashboardStats = {
  users: {
    total: 0,
    active: 0,
    newThisMonth: 0,
    byRole: {
      student: 0,
      organization: 0,
      admin: 0
    }
  },
  events: {
    total: 0,
    upcoming: 0,
    viewsThisMonth: 0,
    byCategory: {
      conference: 0,
      workshop: 0,
      meetup: 0,
      hackathon: 0,
      webinar: 0,
      other: 0
    }
  },
  organizations: {
    total: 0,
    verified: 0,
    pendingVerification: 0
  }
};

const DashboardStats: React.FC<DashboardStatsProps> = ({ onError }) => {
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>({});
  const [categoryLabels, setCategoryLabels] = useState<Record<string, string>>({});

  // Récupérer les statistiques au chargement du composant
  useEffect(() => {
    fetchStats();
    fetchCategoryDetails();
  }, []);

  // Fonction pour récupérer les détails des catégories (couleurs et labels)
  const fetchCategoryDetails = async () => {
    try {
      const categories = await categoryService.getCategories();
      
      // Créer des mappings pour les couleurs et labels
      const colors: Record<string, string> = {};
      const labels: Record<string, string> = {};
      
      categories.forEach(category => {
        colors[category.id] = category.color || '#ADB5BD';
        labels[category.id] = category.name;
      });
      
      setCategoryColors(colors);
      setCategoryLabels(labels);
    } catch (err) {
      console.error('Erreur lors du chargement des détails de catégories:', err);
    }
  };

  // Fonction pour récupérer les statistiques depuis l'API
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminService.getDashboardStats();
      setStats(response);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      const errorMessage = 'Impossible de charger les statistiques du tableau de bord.';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Utiliser les statistiques en veillant à avoir une valeur par défaut
  const safeStats = loading ? defaultStats : stats;

  // S'assurer que les statistiques par catégorie sont complètes
  const ensureAllCategories = (categoryStats: Record<string, number>) => {
    const allCategories = ['conference', 'workshop', 'meetup', 'hackathon', 'webinar', 'other'];
    const result = { ...categoryStats };
    
    allCategories.forEach(category => {
      if (!(category in result)) {
        result[category] = 0;
      }
    });
    
    return result;
  };

  // Fonction pour obtenir la couleur associée à une catégorie d'événement
  const getCategoryColor = (category: string) => {
    // Utiliser le mapping de couleurs chargé depuis l'API si disponible
    if (categoryColors[category]) {
      return categoryColors[category];
    }
    
    // Couleurs par défaut au cas où
    switch (category) {
      case 'conference':
        return '#4C6EF5';
      case 'workshop':
        return '#12B886';
      case 'meetup':
        return '#FA5252'; 
      case 'hackathon':
        return '#7950F2';
      case 'webinar':
        return '#FD7E14';
      default:
        return '#ADB5BD';
    }
  };

  // Fonction pour obtenir le label associé à une catégorie d'événement
  const getCategoryLabel = (category: string) => {
    // Utiliser le mapping de labels chargé depuis l'API si disponible
    if (categoryLabels[category]) {
      return categoryLabels[category];
    }
    
    // Labels par défaut au cas où
    switch (category) {
      case 'conference':
        return 'Conférence';
      case 'workshop':
        return 'Atelier';
      case 'meetup':
        return 'Meetup';
      case 'hackathon':
        return 'Hackathon';
      case 'webinar':
        return 'Webinaire';
      default:
        return 'Autre';
    }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2>Statistiques du tableau de bord</h2>
        {loading && <div className={styles.loadingIndicator}>Chargement...</div>}
        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.statRow}>
        {/* Statistiques des utilisateurs */}
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <IconUsers size={24} className={styles.statIcon} />
            <h3>Utilisateurs</h3>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{safeStats.users.total}</div>
            <div className={styles.statLabel}>Total</div>
          </div>
          <div className={styles.statDetails}>
            <div className={styles.statItem}>
              <div className={styles.statItemLabel}>Actifs</div>
              <div className={styles.statItemValue}>{safeStats.users.active}</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statItemLabel}>Nouveaux</div>
              <div className={styles.statItemValue}>{safeStats.users.newThisMonth}</div>
            </div>
          </div>
          <div className={styles.statDistribution}>
            <div className={styles.statDistributionItem} style={{ width: `${(safeStats.users.byRole.student / safeStats.users.total) * 100}%`, backgroundColor: '#4C6EF5' }}>
              <span className={styles.statDistributionTooltip}>Étudiants: {safeStats.users.byRole.student}</span>
            </div>
            <div className={styles.statDistributionItem} style={{ width: `${(safeStats.users.byRole.organization / safeStats.users.total) * 100}%`, backgroundColor: '#12B886' }}>
              <span className={styles.statDistributionTooltip}>Organisations: {safeStats.users.byRole.organization}</span>
            </div>
            <div className={styles.statDistributionItem} style={{ width: `${(safeStats.users.byRole.admin / safeStats.users.total) * 100}%`, backgroundColor: '#FA5252' }}>
              <span className={styles.statDistributionTooltip}>Admins: {safeStats.users.byRole.admin}</span>
            </div>
          </div>
        </div>

        {/* Statistiques des événements */}
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <IconCalendarEvent size={24} className={styles.statIcon} />
            <h3>Événements</h3>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{safeStats.events.total}</div>
            <div className={styles.statLabel}>Total</div>
          </div>
          <div className={styles.statDetails}>
            <div className={styles.statItem}>
              <div className={styles.statItemLabel}>À venir</div>
              <div className={styles.statItemValue}>{safeStats.events.upcoming}</div>
            </div>
            {safeStats.events.viewsThisMonth && (
              <div className={styles.statItem}>
                <div className={styles.statItemLabel}>Vues</div>
                <div className={styles.statItemValue}>
                  <IconEye size={14} />
                  {safeStats.events.viewsThisMonth}
                </div>
              </div>
            )}
          </div>
          <div className={styles.statDistribution}>
            {Object.entries(ensureAllCategories(safeStats.events.byCategory)).map(([category, count]) => {
              // Obtenir la couleur et le label associés à cette catégorie
              const categoryColor = getCategoryColor(category);
              const categoryLabel = getCategoryLabel(category);
              
              // Ne pas afficher les catégories sans événements
              if (count === 0) return null;
              
              return (
                <div key={category} 
                  className={styles.statDistributionItem} 
                  style={{ 
                    width: `${(count as number / safeStats.events.total) * 100}%`, 
                    backgroundColor: categoryColor 
                  }}
                >
                  <span className={styles.statDistributionTooltip}>
                    {categoryLabel}: {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Statistiques des organisations */}
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <IconBuildingStore size={24} className={styles.statIcon} />
            <h3>Organisations</h3>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{safeStats.organizations.total}</div>
            <div className={styles.statLabel}>Total</div>
          </div>
          <div className={styles.statDetails}>
            <div className={styles.statItem}>
              <div className={styles.statItemLabel}>Vérifiées</div>
              <div className={styles.statItemValue}>{safeStats.organizations.verified}</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statItemLabel}>En attente</div>
              <div className={styles.statItemValue}>{safeStats.organizations.pendingVerification}</div>
            </div>
          </div>
          <div className={styles.statDistribution}>
            <div className={styles.statDistributionItem} style={{ width: `${(safeStats.organizations.verified / safeStats.organizations.total) * 100}%`, backgroundColor: '#12B886' }}>
              <span className={styles.statDistributionTooltip}>Vérifiées: {safeStats.organizations.verified}</span>
            </div>
            <div className={styles.statDistributionItem} style={{ width: `${(safeStats.organizations.pendingVerification / safeStats.organizations.total) * 100}%`, backgroundColor: '#FD7E14' }}>
              <span className={styles.statDistributionTooltip}>En attente: {safeStats.organizations.pendingVerification}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
